import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getAssessmentIdByModuleId } from '@/utils/assessmentLookup';

const ADVISOR_LABELS: Record<string, string> = {
  accountant_cpa: 'Accountant / CPA',
  tax_planning_specialist: 'Tax Planning Specialist',
  bookkeeper: 'Bookkeeper',
  cross_border_tax: 'Cross-Border Tax Advisor',
  cfo_fractional: 'CFO / Fractional CFO',
  other_financial: 'Other Financial / Tax Advisor',
  corporate_business_lawyer: 'Corporate / Business Lawyer',
  estate_planning_lawyer: 'Estate Planning Lawyer',
  tax_lawyer: 'Tax Lawyer',
  securities_regulatory_lawyer: 'Securities / Regulatory Lawyer',
  employment_lawyer: 'Employment Lawyer',
  ip_lawyer: 'Intellectual Property Lawyer',
  partnership_shareholder_specialist: 'Partnership / Shareholder Specialist',
  other_legal: 'Other Legal Advisor',
  business_valuator_cbv: 'Chartered Business Valuator (CBV)',
  ma_advisor: 'M&A Advisor',
  succession_consultant: 'Succession Planning Consultant',
  dealer_liaison: 'Dealer / Broker Succession Liaison',
  transition_coach: 'Business Transition Coach',
  other_succession: 'Other Succession Specialist',
  personal_financial_planner: 'Personal Financial Planner',
  insurance_advisor: 'Insurance Advisor',
  wealth_manager: 'Wealth Manager / Financial Planner',
  estate_trustee: 'Estate Trustee / Trust Officer',
  risk_management_consultant: 'Risk Management Consultant',
  other_wealth: 'Other Wealth / Risk Advisor',
};

function extractString(val: unknown): string | null {
  if (typeof val === 'string' && val.trim()) return val.trim();
  if (val && typeof val === 'object' && 'value' in (val as any)) {
    const inner = (val as any).value;
    if (typeof inner === 'string' && inner.trim()) return inner.trim();
  }
  return null;
}

/**
 * Syncs advisor contact data from A6 assessment responses into team_members.
 * Called once on A6 completion. Idempotent — deletes previous a6_import rows first.
 */
export async function syncAdvisorsToTeamMembers(
  userId: string,
  effectiveTeamId: string
): Promise<void> {
  console.log('[A6 Sync] Starting advisor sync to team_members');

  // Resolve A6 assessment UUID dynamically
  const a6AssessmentId = await getAssessmentIdByModuleId('advisory-team');
  if (!a6AssessmentId) {
    console.error('[A6 Sync] Failed to resolve assessment UUID for module "advisory-team". Aborting sync.');
    return;
  }

  // 1. Fetch all A6 responses
  const { data: responses, error: respError } = await supabase
    .from('user_assessment_responses')
    .select('question_id, response_value')
    .eq('user_id', userId)
    .eq('assessment_id', a6AssessmentId);

  if (respError || !responses?.length) {
    console.warn('[A6 Sync] No responses found or error:', respError?.message);
    return;
  }

  // 2. Resolve question keys
  const questionIds = responses.map((r) => r.question_id);
  const { data: questions, error: qError } = await supabase
    .from('assessment_questions')
    .select('id, question_key')
    .in('id', questionIds);

  if (qError || !questions) {
    console.error('[A6 Sync] Failed to fetch question keys:', qError?.message);
    return;
  }

  const keyMap = new Map(questions.map((q) => [q.id, q.question_key]));
  const answersByKey: Record<string, any> = {};
  for (const row of responses) {
    const key = keyMap.get(row.question_id);
    if (key) answersByKey[key] = row.response_value;
  }

  // 3. Determine selected advisor types from master gate
  const selectedTypes: string[] = Array.isArray(answersByKey['advisor_team_members'])
    ? answersByKey['advisor_team_members']
    : [];

  if (selectedTypes.length === 0) {
    console.log('[A6 Sync] No advisor types selected, cleaning up old imports');
  }

  // 4. Build rows for RPC
  const rows = selectedTypes.length === 0
    ? []
    : selectedTypes
        .map((advisorType) => {
          const name = extractString(answersByKey[`${advisorType}_name`]);
          if (!name) return null;

          return {
            advisor_type: advisorType,
            full_name: name,
            title: extractString(answersByKey[`${advisorType}_title`]) || null,
            email_address: extractString(answersByKey[`${advisorType}_email`]) || null,
            firm_name: extractString(answersByKey[`${advisorType}_firm`]) || null,
            phone_number: extractString(answersByKey[`${advisorType}_phone`]) || null,
            member_role: ADVISOR_LABELS[advisorType] || advisorType.replace(/_/g, ' '),
          };
        })
        .filter(Boolean);

  if (selectedTypes.length > 0 && rows.length === 0) {
    console.log('[A6 Sync] No advisor rows to sync (no names provided)');
  }

  console.log('[A6 Sync] Rows to sync via RPC:', JSON.stringify(rows, null, 2));

  // 5. Call SECURITY DEFINER RPC (handles DELETE + INSERT internally)
  const { error } = await supabase.rpc('sync_a6_advisors_to_team_members', {
    p_team_id: effectiveTeamId,
    p_advisors: rows,
  } as any);

  if (error) {
    toast({ title: 'Sync failed', description: error.message, variant: 'destructive' });
    console.error('sync_a6_advisors_to_team_members error:', error);
    return;
  }

  // Recalculate scores after team_members changed
  const { calculateScores } = await import('@/services/scoring/calculateScores');
  try {
    await calculateScores(userId, effectiveTeamId, a6AssessmentId);
  } catch (err) {
    console.error('[syncAdvisorsToTeamMembers] calculateScores failed:', err);
  }
}
